import { NavigateFn } from '@tanstack/react-router'
import { Column, ColumnDef, ColumnFiltersState } from '@tanstack/react-table'
import { ComboboxFilter } from 'components/table/enhancements/filters/ComboboxFilter'
import { DateFilter } from 'components/table/enhancements/filters/DateFilter'
import { MultiSelectFilter } from 'components/table/enhancements/filters/MultiSelectFilter'
import { NumberFilter } from 'components/table/enhancements/filters/NumberFilter'
import { StringFilter } from 'components/table/enhancements/filters/StringFilter'
import React from 'react'
import styled from 'styled-components'
import { InputType } from 'types'

const ActiveFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  gap: 0.5rem;
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: none; /* Firefox */
  align-items: center;

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  &:has(div[data-input-visible='true']) {
    overflow: visible;
  }
`

const FilterComponentContainer = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  width: max-content;

  & button {
    margin-bottom: 0 !important;
    width: max-content;
  }
`

const getFilterComponent = (inputType: InputType | undefined) => {
  switch (inputType) {
    case InputType.Text:
      return StringFilter
    case InputType.MultiSelect:
      return MultiSelectFilter
    case InputType.Date:
      return DateFilter
    case InputType.Number:
      return NumberFilter
    case InputType.Combobox:
      return ComboboxFilter
    default:
      return null
  }
}

export const ActiveFilters = <TData,>({
  columns,
  activeFilters,
  setActiveFilters,
  getColumn,
  navigate,
  disableUrlFilters,
  inputVisible = true,
  showAllOption = false,
}: {
  columns: ColumnDef<TData>[]
  activeFilters: string[]
  setActiveFilters: React.Dispatch<React.SetStateAction<string[]>>
  getColumn: (columnID: string) => Column<TData, unknown> | undefined
  initialColumnFilters?: ColumnFiltersState
  navigate: NavigateFn
  disableUrlFilters?: boolean
  inputVisible?: boolean
  showAllOption?: boolean
}) => {
  return (
    <ActiveFiltersContainer>
      {activeFilters.map((filterID) => {
        const columnDef = columns.find((col) => col.id === filterID)
        const column = getColumn(filterID)
        if (!columnDef || !column) return null
        const FilterComponent = getFilterComponent(columnDef.meta?.inputType)
        if (!FilterComponent) return null
        return (
          <FilterComponentContainer key={filterID}>
            <FilterComponent
              column={column}
              columnHeader={String(columnDef.header)}
              setActiveFilters={setActiveFilters}
              navigate={navigate}
              disableUrlFilters={disableUrlFilters}
              inputVisible={inputVisible}
              showAllOption={showAllOption}
            />
          </FilterComponentContainer>
        )
      })}
    </ActiveFiltersContainer>
  )
}
